import type { BodyShape, FaceShape } from '../../../shared/game/world-types.js';
import { FACE_SHAPE_RANGES } from '../../../shared/game/character-build.js';
import type { HumanFace } from './human.js';

/**
 * The face sliders (`FaceShape`): continuous edits over whichever face a look chose (a preset such as Angular or an ancestry's own), turned into
 * the face warp. They move the skin, eyes, paint and facial hair together and never the skull above the brow, so every hairstyle and helmet still
 * fits and no new head is sculpted: a slider costs nothing to draw and almost nothing to build. Wrinkles and sag are the aging layer; a look
 * that leaves them out takes them from its body's age.
 */
export type FaceShapeKey = keyof FaceShape;
export const FACE_SHAPE_NEUTRAL: FaceShape = {
  length: 0, width: 0, jaw: 0, chin: 0, cheekbones: 0, brow: 0, noseSize: 0, noseWidth: 0, noseBridge: 0, eyeSpacing: 0, eyeDepth: 0, eyeSize: 0, lips: 0, wrinkles: 0, sag: 0,
};
/** The creator's sliders, in the order it shows them: the shape of the face, then its age. Each names its two ends. */
export const FACE_SHAPE_FIELDS: { key: FaceShapeKey; label: string; ends: [string, string]; aging?: true; min: number; max: number }[] = ([
  ['length', 'Face length', ['Short', 'Long']], ['width', 'Face width', ['Narrow', 'Broad']], ['jaw', 'Jaw', ['Tapered', 'Square']], ['chin', 'Chin', ['Receding', 'Jutting']],
  ['cheekbones', 'Cheekbones', ['Flat', 'High']], ['brow', 'Brow', ['Smooth', 'Heavy']], ['noseSize', 'Nose size', ['Small', 'Large']], ['noseWidth', 'Nose width', ['Narrow', 'Wide']],
  ['noseBridge', 'Nose bridge', ['Dished', 'Hooked']], ['eyeSpacing', 'Eye spacing', ['Close', 'Wide']], ['eyeDepth', 'Eye set', ['Prominent', 'Deep']], ['eyeSize', 'Eye size', ['Small', 'Large']],
  ['lips', 'Lips', ['Thin', 'Full']], ['wrinkles', 'Wrinkles', ['Smooth', 'Deep'], true], ['sag', 'Sag', ['Firm', 'Heavy'], true],
] as const).map(([key, label, ends, aging]) => ({ key, label, ends: [...ends], ...aging ? { aging } : {}, min: FACE_SHAPE_RANGES[key][0], max: FACE_SHAPE_RANGES[key][1] }));
const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
const smooth = (value: number, from: number, to: number) => { const t = clamp((value - from) / (to - from), 0, 1); return t * t * (3 - 2 * t); };

/** How lined and how slack a face of this body age is when its look sets neither: smooth to about forty, deep lines and jowls by eighty. */
export function ageAging(age: number): Pick<FaceShape, 'wrinkles' | 'sag'> {
  return { wrinkles: Math.round(smooth(age, .15, .95) * 100) / 100, sag: Math.round(.9 * smooth(age, .3, 1) * 100) / 100 };
}
/** A look's sliders, each within its range: missing or broken shape sliders are neutral, missing aging follows the body's age. */
export function faceShape(shape?: Partial<FaceShape>, body?: Partial<BodyShape>): FaceShape {
  const resolved = { ...FACE_SHAPE_NEUTRAL, ...ageAging(typeof body?.age === 'number' && Number.isFinite(body.age) ? clamp(body.age, 0, 1) : 0) };
  for (const { key, min, max } of FACE_SHAPE_FIELDS) { const value = shape?.[key]; if (typeof value === 'number' && Number.isFinite(value)) resolved[key] = clamp(value, min, max); }
  return resolved;
}
/** A word for where a slider stands: one end, the other, or the middle. Aging sliders read from none upward. */
export function faceShapeWord(key: FaceShapeKey, value: number) {
  const field = FACE_SHAPE_FIELDS.find(candidate => candidate.key === key)!;
  if (field.aging) return value < .1 ? field.ends[0] : value < .4 ? 'Slight' : value < .75 ? 'Marked' : field.ends[1];
  return Math.abs(value) < .15 ? 'Average' : `${Math.abs(value) < .55 ? 'Slightly ' + field.ends[value < 0 ? 0 : 1].toLowerCase() : field.ends[value < 0 ? 0 : 1]}`;
}

/** What the warp needs beyond `HumanFace`: how far the eyes move apart and how much the soft tissue has fallen. */
export interface FaceWarpExtras { eyeSpacing: number; sag: number }
/**
 * The chosen face (`face`, already a preset or an ancestry's own) with the sliders laid over it. Only measures the face warp reads move, so the
 * sculpt is shared with the unslid face. Sag also thins the lips and lengthens the nose a little; the rest of it (jowls, fallen cheeks, eye bags,
 * the fold from nose to mouth) and eye spacing are warp terms of their own (`FaceWarpExtras`).
 */
export function slideFace(face: HumanFace, shape: FaceShape): HumanFace {
  const f = { ...face }, s = shape;
  f.length *= 1 + .075 * s.length; f.width *= 1 + .065 * s.width;
  f.jaw *= 1 + .09 * s.jaw; f.square = Math.max(0, f.square + .45 * s.jaw);
  f.chinDepth += .0055 * s.chin; f.chin *= 1 + .05 * s.chin;
  f.cheek = Math.max(0, f.cheek + .012 * s.cheekbones); f.hollow = Math.max(0, f.hollow + .0022 * s.cheekbones);
  f.brow = Math.max(0, f.brow + .0042 * s.brow);
  f.noseSize *= 1 + .13 * s.noseSize; f.nose *= 1 + .2 * s.noseWidth; f.bridge += .005 * s.noseBridge;
  f.eye = Math.max(0, f.eye + .0045 * s.eyeDepth); f.eyeSize *= 1 + .11 * s.eyeSize;
  f.lips *= (1 + .26 * s.lips) * (1 - .18 * s.sag); f.noseLength += .05 * s.sag;
  f.fullness = Math.max(0, f.fullness - .0008 * s.sag);
  return f;
}
export function faceWarpExtras(shape: FaceShape): FaceWarpExtras { return { eyeSpacing: shape.eyeSpacing, sag: shape.sag }; }

/**
 * Sliders for a fresh random face: most land near the middle and a few go far, so faces differ without turning into caricatures. `random`
 * returns values in [0, 1). Aging is left out, so it keeps following the body's age.
 */
export function randomFaceShape(random: () => number = Math.random): Partial<FaceShape> {
  const shape: Partial<FaceShape> = {};
  for (const { key, aging } of FACE_SHAPE_FIELDS) {
    if (aging) continue;
    // A triangular spread, widened now and then: about one slider in six lands past three quarters.
    const value = (random() + random() - 1) * (random() < .25 ? 1.3 : .85);
    shape[key] = Math.round(clamp(value, -1, 1) * 20) / 20;
  }
  return shape;
}
